import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ConfirmModal from './ConfirmModal';
import SettingsMessageRelay from './SettingsMessageRelay';
import SettingsStatsIngest from './SettingsStatsIngest';
import SettingsDiscordOps from './SettingsDiscordOps';
import SettingsMrpRating from './SettingsMrpRating';
import SettingsDisplay from './SettingsDisplay';
import SettingsDataExport from './SettingsDataExport';
import { ServerIcon } from '../utils/serverIcons';
import {
  DEFAULT_DP_PLAN_SETTINGS,
  normalizeDpPlanSettings,
  DP_PLAN_ACTIONS,
} from '../utils/dpPlanSettings';
import {
  DEFAULT_STAFF_LIST_SETTINGS,
  STAFF_LIST_SETTING_KEY,
  normalizeStaffListSettings,
} from '../utils/staffListSettings';
import { DISCORD_OPS_SETTING_KEY } from '../utils/discordOpsSettings';
import './Settings.css';
import './FormModal.css';

function MrpRatingToggle({ label, description, checked, onChange }) {
  return (
    <label className="mrp-rating-toggle">
      <input type="checkbox" className="mrp-rating-toggle-input" checked={checked} onChange={onChange} />
      <span className="mrp-rating-toggle-track" aria-hidden="true">
        <span className="mrp-rating-toggle-thumb" />
      </span>
      <span className="mrp-rating-toggle-text">
        <span className="mrp-rating-toggle-label">{label}</span>
        {description ? <span className="mrp-rating-toggle-desc">{description}</span> : null}
      </span>
    </label>
  );
}

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('servers');
  const [servers, setServers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mcSettingsLoading, setMcSettingsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [formData, setFormData] = useState({
    serverNumber: '',
    serverName: '',
    icon: undefined
  });
  const [iconPreviewUrl, setIconPreviewUrl] = useState(null);
  const [mcSettings, setMcSettings] = useState({
    MRP: { ...DEFAULT_DP_PLAN_SETTINGS },
  });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [staffAssignments, setStaffAssignments] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffListSettings, setStaffListSettings] = useState(() =>
    normalizeStaffListSettings(DEFAULT_STAFF_LIST_SETTINGS)
  );
  const [staffLoading, setStaffLoading] = useState(true);
  const [staffSaving, setStaffSaving] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionFormData, setQuestionFormData] = useState({
    question_text: '',
    answer_text: '',
    is_intro: false,
    display_order: 0
  });
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const savedScrollPosition = React.useRef(null);
  const [userRoles, setUserRoles] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  const hasFullSettingsAccess = userRoles.includes('Chief Manager') || userRoles.includes('admin');
  const hasMessageRelayAccess =
    userRoles.includes('Deputy Chief Manager') ||
    userRoles.includes('Chief Manager') ||
    userRoles.includes('admin');
  const canAccessQuestionsTab = hasFullSettingsAccess || hasMessageRelayAccess;

  const loadUserRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/check-roles', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const roles = data.roles || [];
      setUserRoles(roles);

      const hasFullAccess = roles.includes('Chief Manager') || roles.includes('admin');
      const hasRelayAccess =
        roles.includes('Deputy Chief Manager') ||
        roles.includes('Chief Manager') ||
        roles.includes('admin');

      if (!hasFullAccess && hasRelayAccess) {
        setActiveTab('message-relay');
      }
    } catch (error) {
      console.error('Error loading user roles:', error);
    } finally {
      setRolesLoaded(true);
    }
  };

  useEffect(() => {
    loadUserRoles();
  }, []);

  useEffect(() => {
    if (!rolesLoaded) return;
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'message-relay' && hasMessageRelayAccess) {
      setActiveTab('message-relay');
    }
    if (tab === 'stats-ingest' && hasMessageRelayAccess) {
      setActiveTab('stats-ingest');
    }
    if (tab === 'data-export' && hasFullSettingsAccess) {
      setActiveTab('data-export');
    }
  }, [rolesLoaded, location.search, hasMessageRelayAccess, hasFullSettingsAccess]);

  useEffect(() => {
    if (!rolesLoaded) {
      return;
    }

    if (hasFullSettingsAccess) {
      fetchServers();
      return;
    }

    if (!canAccessQuestionsTab && !hasMessageRelayAccess) {
      setError(
        'Доступ запрещён: требуются роли Deputy Chief Manager, Chief Manager или admin',
      );
      setTimeout(() => {
        navigate('/discordmoderation/overview');
      }, 2000);
    }
  }, [rolesLoaded, hasFullSettingsAccess, canAccessQuestionsTab, hasMessageRelayAccess, navigate]);

  useEffect(() => {
    if (activeTab === 'mc-settings') {
      fetchMcSettings();
    } else if (activeTab === 'staff') {
      fetchStaffData();
    } else if (activeTab === 'questions') {
      fetchQuestions();
    }
  }, [activeTab]);

  // Восстанавливаем позицию прокрутки после обновления списка вопросов
  useEffect(() => {
    if (activeTab === 'questions' && savedScrollPosition.current !== null) {
      const position = savedScrollPosition.current;
      savedScrollPosition.current = null;
      setTimeout(() => {
        window.scrollTo({
          top: position,
          behavior: 'auto'
        });
      }, 50);
    }
  }, [questions, activeTab]);

  const fetchServers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/servers', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.error || 'Доступ запрещён: для доступа к настройкам требуются права Chief Manager или admin');
          setTimeout(() => {
            navigate('/discordmoderation/overview');
          }, 2000);
          return;
        }
        throw new Error('Ошибка при загрузке серверов');
      }

      const data = await response.json();
      setServers(data);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Фильтрация серверов
  const filteredServers = servers.filter(server => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const serverDisplay = `RU${server.server_number}_${server.server_name}`.toLowerCase();
    return (
      server.server_number?.toLowerCase().includes(searchLower) ||
      server.server_name?.toLowerCase().includes(searchLower) ||
      serverDisplay.includes(searchLower)
    );
  }).sort((a, b) => {
    // Сортировка по номеру сервера (числовая)
    const numA = parseInt(a.server_number) || 0;
    const numB = parseInt(b.server_number) || 0;
    if (numA !== numB) {
      return numA - numB;
    }
    // Если номера равны, сортируем по названию
    return (a.server_name || '').localeCompare(b.server_name || '');
  });

  // Пагинация
  const totalPages = Math.ceil(filteredServers.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const paginatedServers = filteredServers.slice(startIndex, startIndex + itemsPerPage);

  // Сброс страницы при изменении поиска
  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const url = editingId
        ? `/api/servers/${editingId}`
        : '/api/servers';
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          serverNumber: formData.serverNumber,
          serverName: formData.serverName,
          ...(formData.icon !== undefined && { icon: formData.icon })
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Ошибка при сохранении');
      }

      setSuccess(editingId ? 'Сервер обновлён' : 'Сервер добавлен');
      setTimeout(() => {
        resetForm();
        fetchServers();
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (server) => {
    setEditingId(server.id);
    setFormData({
      serverNumber: server.server_number,
      serverName: server.server_name,
      icon: undefined
    });
    setIconPreviewUrl(null);
    setShowForm(true);
    if (server.icon_url) {
      const token = localStorage.getItem('token');
      fetch(server.icon_url, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.ok ? r.blob() : null)
        .then((blob) => {
          if (blob) {
            const reader = new FileReader();
            reader.onload = () => setFormData((prev) => ({ ...prev, icon: reader.result }));
            reader.readAsDataURL(blob);
          }
        })
        .catch(() => {});
    }
  };

  const handleDelete = (id) => {
    setConfirmModal({
      isOpen: true,
      title: 'Подтвердите действие',
      message: 'Вы уверены, что хотите удалить этот сервер?',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/servers/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Ошибка при удалении');
          }

          setSuccess('Сервер удалён');
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
          fetchServers();
        } catch (err) {
          setError(err.message);
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setShowForm(false);
    setIconPreviewUrl(null);
    setFormData({
      serverNumber: '',
      serverName: '',
      icon: undefined
    });
  };

  const handleAddClick = () => {
    resetForm();
    setShowForm(true);
  };

  const formatServerDisplay = (server) => {
    return `RU${server.server_number}_${server.server_name}`;
  };

  const fetchMcSettings = async () => {
    try {
      setMcSettingsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/majestic-coins-settings', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.error || 'Доступ запрещён: для доступа к настройкам требуются права Chief Manager или admin');
          setTimeout(() => {
            navigate('/discordmoderation/overview');
          }, 2000);
          return;
        }
        throw new Error('Ошибка при загрузке настроек');
      }

      const data = await response.json();
      setMcSettings({
        MRP: normalizeDpPlanSettings(data.MRP || DEFAULT_DP_PLAN_SETTINGS),
      });
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setMcSettingsLoading(false);
    }
  };

  const editableDpValue = (val) => {
    if (val === '' || val === null || val === undefined || val === 0) return '';
    return String(val);
  };

  const handleDpRewardChange = (field, value) => {
    const clean = value.replace(/[^\d]/g, '');
    setMcSettings((prev) => {
      const mrp = normalizeDpPlanSettings(prev.MRP);
      return {
        ...prev,
        MRP: {
          ...mrp,
          [field]: clean === '' ? '' : Math.max(0, parseInt(clean, 10) || 0),
        },
      };
    });
  };

  const handleDpRewardBlur = (field) => {
    setMcSettings((prev) => {
      const mrp = normalizeDpPlanSettings(prev.MRP);
      const raw = prev.MRP?.[field];
      return {
        ...prev,
        MRP: {
          ...mrp,
          [field]: Math.max(0, parseInt(raw, 10) || 0),
        },
      };
    });
  };

  const handleDpPlanTargetChange = (planKey, metricKey, value) => {
    const clean = value.replace(/[^\d]/g, '');
    setMcSettings((prev) => {
      const mrp = normalizeDpPlanSettings(prev.MRP);
      return {
        ...prev,
        MRP: {
          ...mrp,
          [planKey]: {
            ...mrp[planKey],
            [metricKey]: clean === '' ? '' : Math.max(0, parseInt(clean, 10) || 0),
          },
        },
      };
    });
  };

  const handleDpPlanTargetBlur = (planKey, metricKey) => {
    setMcSettings((prev) => {
      const mrp = normalizeDpPlanSettings(prev.MRP);
      const raw = prev.MRP?.[planKey]?.[metricKey];
      return {
        ...prev,
        MRP: {
          ...mrp,
          [planKey]: {
            ...mrp[planKey],
            [metricKey]: Math.max(0, parseInt(raw, 10) || 0),
          },
        },
      };
    });
  };

  const handleMcSettingsSave = async () => {
    try {
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      const payload = {
        MRP: normalizeDpPlanSettings(mcSettings.MRP),
      };
      const response = await fetch('/api/majestic-coins-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ settings: payload })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Ошибка при сохранении настроек');
      }

      if (data.MRP) {
        setMcSettings({ MRP: normalizeDpPlanSettings(data.MRP) });
      }

      setSuccess('Настройки успешно сохранены');
      setTimeout(() => {
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchStaffData = async () => {
    try {
      setStaffLoading(true);
      const token = localStorage.getItem('token');

      const [assignmentsResponse, usersResponse, settingsResponse] = await Promise.all([
        fetch('/api/staff', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch('/api/staff/users', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch('/api/app-settings', {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!assignmentsResponse.ok) {
        if (assignmentsResponse.status === 403) {
          const errorData = await assignmentsResponse.json().catch(() => ({}));
          setError(errorData.error || 'Доступ запрещён: для доступа к настройкам требуются права Chief Manager или admin');
          setTimeout(() => {
            navigate('/discordmoderation/overview');
          }, 2000);
          return;
        }
        throw new Error('Ошибка при загрузке состава');
      }

      if (!usersResponse.ok) {
        throw new Error('Ошибка при загрузке пользователей');
      }

      const assignmentsData = await assignmentsResponse.json();
      const usersData = await usersResponse.json();
      const settingsData = settingsResponse.ok ? await settingsResponse.json() : {};

      setStaffAssignments(Array.isArray(assignmentsData) ? assignmentsData.slice(0, 5) : []);
      setStaffUsers(usersData);
      {
        const staffRaw = settingsData[STAFF_LIST_SETTING_KEY] || DEFAULT_STAFF_LIST_SETTINGS;
        const opsCh = settingsData[DISCORD_OPS_SETTING_KEY]?.staff_list_channel_id;
        setStaffListSettings(
          normalizeStaffListSettings({
            ...staffRaw,
            channel_id: opsCh || staffRaw.channel_id || '',
          }),
        );
      }
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setStaffLoading(false);
    }
  };

  const updateStaffAssignmentLocal = (assignmentId, patch) => {
    setStaffAssignments((prev) =>
      prev.map((assignment) =>
        assignment.id === assignmentId ? { ...assignment, ...patch } : assignment
      )
    );
  };

  const handleStaffListSettingsChange = (patch) => {
    setStaffListSettings((prev) => ({ ...prev, ...patch }));
  };

  const handleStaffRoleSectionChange = (index, patch) => {
    setStaffListSettings((prev) => {
      const role_sections = prev.role_sections.map((section, i) =>
        i === index ? { ...section, ...patch } : section
      );
      // Не нормализуем при вводе — иначе textarea глотает пустые строки
      return { ...prev, role_sections };
    });
  };

  const handleAddStaffRoleSection = () => {
    setStaffListSettings((prev) => {
      if (prev.role_sections.length >= 8) return prev;
      return normalizeStaffListSettings({
        ...prev,
        role_sections: [
          ...prev.role_sections,
          { title: 'Раздел', roles: [], show_reprimands: true },
        ],
      });
    });
  };

  const handleRemoveStaffRoleSection = (index) => {
    setStaffListSettings((prev) =>
      normalizeStaffListSettings({
        ...prev,
        role_sections: prev.role_sections.filter((_, i) => i !== index),
      })
    );
  };

  const handleStaffSave = async () => {
    try {
      setStaffSaving(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      const normalizedList = normalizeStaffListSettings(staffListSettings);

      // Сначала должности без обновления Discord, потом настройки (они дергают Discord один раз)
      const updatedAssignments = [];
      for (const assignment of staffAssignments.slice(0, 5)) {
        const response = await fetch(`/api/staff/${assignment.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            user_discord_id: assignment.user_discord_id || null,
            display_name: (assignment.display_name || '').trim() || assignment.position,
            add_spacer_after: Boolean(assignment.add_spacer_after),
            show_reprimands_in_discord: assignment.show_reprimands_in_discord !== false,
            skip_discord_update: true,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Ошибка при сохранении должности');
        }
        updatedAssignments.push({
          ...assignment,
          display_name: data.display_name,
          user_discord_id: data.user_discord_id,
          add_spacer_after: data.add_spacer_after,
          show_reprimands_in_discord: data.show_reprimands_in_discord,
          user: data.user,
        });
      }

      const settingsResponse = await fetch(`/api/app-settings/${STAFF_LIST_SETTING_KEY}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ setting_value: normalizedList }),
      });
      const settingsData = await settingsResponse.json().catch(() => ({}));
      if (!settingsResponse.ok) {
        throw new Error(settingsData.error || 'Ошибка при сохранении канала и ролей');
      }

      setStaffAssignments(updatedAssignments);
      setStaffListSettings(
        normalizeStaffListSettings(settingsData.setting_value || normalizedList)
      );
      setSuccess('Настройки состава сохранены');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setStaffSaving(false);
    }
  };

  const handleStaffRefreshDiscord = async () => {
    try {
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      const response = await fetch('/api/staff/update-discord-list', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Не удалось обновить список в Discord');
      }
      setSuccess(data.message || 'Список состава обновлён в Discord');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  // Функции для работы с вопросами
  const fetchQuestions = async () => {
    try {
      setQuestionsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/junior-call-questions', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка при загрузке вопросов');
      }

      const data = await response.json();
      setQuestions(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setQuestionFormData({
      question_text: '',
      answer_text: '',
      is_intro: false,
      display_order: 0
    });
    setShowQuestionForm(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionFormData({
      question_text: question.question_text,
      answer_text: question.answer_text || '',
      is_intro: question.is_intro,
      display_order: question.display_order
    });
    setShowQuestionForm(true);
  };

  const handleDeleteQuestion = (questionId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Удаление вопроса',
      message: 'Вы уверены, что хотите удалить этот вопрос?',
      onConfirm: async () => {
        try {
          // Сохраняем позицию прокрутки перед удалением
          savedScrollPosition.current = window.scrollY || document.documentElement.scrollTop;
          
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/junior-call-questions/${questionId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            throw new Error('Ошибка при удалении вопроса');
          }

          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
          
          // Обновляем список вопросов
          await fetchQuestions();
          
          setSuccess('Вопрос успешно удален');
          setTimeout(() => setSuccess(''), 2000);
        } catch (err) {
          setError(err.message);
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
          savedScrollPosition.current = null;
        }
      }
    });
  };

  const handleSaveQuestion = async () => {
    try {
      setError('');
      // Сохраняем позицию прокрутки перед сохранением
      savedScrollPosition.current = window.scrollY || document.documentElement.scrollTop;
      
      const token = localStorage.getItem('token');
      const url = editingQuestion 
        ? `/api/junior-call-questions/${editingQuestion.id}`
        : '/api/junior-call-questions';
      
      const method = editingQuestion ? 'PUT' : 'POST';
      
      // При создании нового вопроса всегда is_intro = false
      // При редактировании сохраняем текущее значение is_intro из редактируемого вопроса
      const dataToSend = editingQuestion 
        ? { ...questionFormData, is_intro: editingQuestion.is_intro }
        : { ...questionFormData, is_intro: false };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Ошибка при сохранении вопроса');
      }

      // Закрываем модальное окно перед обновлением списка
      setShowQuestionForm(false);
      setEditingQuestion(null);
      
      // Обновляем список вопросов
      await fetchQuestions();
      
      setSuccess(editingQuestion ? 'Вопрос успешно обновлен' : 'Вопрос успешно создан');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.message);
      savedScrollPosition.current = null;
    }
  };

  const resetQuestionForm = () => {
    setShowQuestionForm(false);
    setEditingQuestion(null);
    setQuestionFormData({
      question_text: '',
      answer_text: '',
      is_intro: false,
      display_order: 0
    });
  };

  return (
    <div className="settings">
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <nav className="settings-nav" aria-label="Разделы настроек">
        {hasFullSettingsAccess && (
          <>
            <button type="button" className={`settings-nav__item ${activeTab === 'servers' ? 'is-active' : ''}`} onClick={() => setActiveTab('servers')}>Серверы</button>
            <button type="button" className={`settings-nav__item ${activeTab === 'staff' ? 'is-active' : ''}`} onClick={() => setActiveTab('staff')}>Состав</button>
            <button type="button" className={`settings-nav__item ${activeTab === 'discord-ops' ? 'is-active' : ''}`} onClick={() => setActiveTab('discord-ops')}>Каналы</button>
            <button type="button" className={`settings-nav__item ${activeTab === 'mc-settings' ? 'is-active' : ''}`} onClick={() => setActiveTab('mc-settings')}>DP</button>
            <span className="settings-nav__sep" aria-hidden="true" />
            <button type="button" className={`settings-nav__item ${activeTab === 'mrp-rating' ? 'is-active' : ''}`} onClick={() => setActiveTab('mrp-rating')}>Рейтинг</button>
            <button type="button" className={`settings-nav__item ${activeTab === 'display-settings' ? 'is-active' : ''}`} onClick={() => setActiveTab('display-settings')}>Отображение</button>
            <button type="button" className={`settings-nav__item ${activeTab === 'data-export' ? 'is-active' : ''}`} onClick={() => setActiveTab('data-export')}>Выгрузки</button>
          </>
        )}
        {hasMessageRelayAccess && (
          <>
            {(hasFullSettingsAccess) && <span className="settings-nav__sep" aria-hidden="true" />}
            <button type="button" className={`settings-nav__item ${activeTab === 'stats-ingest' ? 'is-active' : ''}`} onClick={() => setActiveTab('stats-ingest')}>Учёт</button>
            <button type="button" className={`settings-nav__item ${activeTab === 'message-relay' ? 'is-active' : ''}`} onClick={() => setActiveTab('message-relay')}>Трансляция</button>
          </>
        )}
        {canAccessQuestionsTab && (
          <button type="button" className={`settings-nav__item ${activeTab === 'questions' ? 'is-active' : ''}`} onClick={() => setActiveTab('questions')}>Вопросы</button>
        )}
      </nav>

      <div className="settings-body">

      {/* Содержимое вкладки "Серверы" */}
      {activeTab === 'servers' && (
        <>
          <div className="add-button-container">
        <button className="btn btn-add-server" onClick={handleAddClick} type="button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span>Добавить сервер</span>
        </button>
      </div>

      {/* Модальное окно формы */}
      {showForm && (
        <div className="form-modal-overlay" onClick={resetForm}>
          <div className="form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="form-modal-header">
              <h3>{editingId ? 'Редактировать сервер' : 'Добавить сервер'}</h3>
              <button type="button" className="btn-close-form" onClick={resetForm} title="Закрыть">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="form-modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="serverNumber">Номер сервера *</label>
                  <input
                    type="text"
                    id="serverNumber"
                    name="serverNumber"
                    value={formData.serverNumber}
                    onChange={handleInputChange}
                    required
                    placeholder="Например: 1, 2, 3..."
                  />
                  <small className="form-hint">Будет отображаться как RU{formData.serverNumber || 'X'}_...</small>
                </div>

                <div className="form-group">
                  <label htmlFor="serverName">Название сервера *</label>
                  <input
                    type="text"
                    id="serverName"
                    name="serverName"
                    value={formData.serverName}
                    onChange={handleInputChange}
                    required
                    placeholder="Например: Main, Test, Development..."
                  />
                  <small className="form-hint">Будет отображаться как RU{formData.serverNumber || 'X'}_{formData.serverName || '...'}</small>
                </div>
              </div>

              <div className="form-group form-group-full">
                <label>Иконка сервера</label>
                <div className="server-icon-upload">
                  {(formData.icon || iconPreviewUrl) && (
                    <div className="server-icon-preview">
                      <img src={formData.icon || iconPreviewUrl} alt="" />
                      <button
                        type="button"
                        className="btn-remove-icon"
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, icon: '' }));
                          setIconPreviewUrl(null);
                        }}
                        title="Удалить иконку"
                      >
                        Удалить иконку
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    id="server-icon-file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => setFormData((prev) => ({ ...prev, icon: reader.result }));
                      reader.readAsDataURL(file);
                      e.target.value = '';
                    }}
                    className="server-icon-input-hidden"
                  />
                  <label htmlFor="server-icon-file" className="btn-upload-icon">
                    Загрузить иконку
                  </label>
                </div>
                <small className="form-hint">PNG, JPG или WebP. Отображается в списке серверов.</small>
              </div>

              <div className="form-preview">
                <div className="preview-label">Предварительный просмотр:</div>
                <div className="preview-value">
                  RU{formData.serverNumber || 'X'}_{formData.serverName || '...'}
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Сохранить изменения' : 'Добавить сервер'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Список серверов */}
      <div className="settings-list-card">
        <div className="card-header">
          <h3>Список серверов ({filteredServers.length})</h3>
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Поиск..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Загрузка...</div>
        ) : filteredServers.length === 0 ? (
          <div className="empty-state">
            {search ? 'Серверы не найдены по запросу' : 'Серверы не найдены'}
          </div>
        ) : (
          <div className="settings-table-container">
            <table className="settings-table">
              <thead>
                <tr>
                  <th>Сервер</th>
                  <th>Номер</th>
                  <th>Название</th>
                  <th>Дата создания</th>
                  <th className="actions-column">Действия</th>
                </tr>
              </thead>
              <tbody>
                {paginatedServers.map((server) => (
                  <tr key={server.id}>
                    <td className="server-display">
                      <ServerIcon server={server} className="settings-server-icon" size={24} />
                      <span>{formatServerDisplay(server)}</span>
                    </td>
                    <td>{server.server_number}</td>
                    <td>{server.server_name}</td>
                    <td>
                      {new Date(server.created_at).toLocaleString('ru-RU', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="actions-column">
                      <div className="action-buttons">
                        <button
                          className="btn-action btn-edit"
                          onClick={() => handleEdit(server)}
                          title="Изменить"
                        >
                          Изменить
                        </button>
                        <button
                          className="btn-action btn-delete"
                          onClick={() => handleDelete(server.id)}
                          title="Удалить"
                        >
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Пагинация */}
        {!loading && filteredServers.length > 0 && totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={page === 1}
            >
              Назад
            </button>
            <span className="pagination-info">
              Страница{' '}
              <input
                type="number"
                className="pagination-input"
                min={1}
                max={totalPages}
                value={page}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') return;
                  const n = parseInt(v, 10);
                  if (!isNaN(n)) setPage(Math.max(1, Math.min(totalPages, n)));
                }}
                aria-label="Номер страницы"
              />
              {' '}из {totalPages}
            </span>
            <button
              className="pagination-btn"
              onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
            >
              Вперёд
            </button>
          </div>
        )}
      </div>
        </>
      )}

      {/* Содержимое вкладки "Настройки DP" */}
      {activeTab === 'mc-settings' && (
        <div className="mc-settings-container dp-plans">
          {mcSettingsLoading ? (
            <div className="loading">Загрузка настроек...</div>
          ) : (
            <>
              <div className="dp-plans__grid">
                <section className="dp-plan-block" aria-labelledby="dp-plan-min-title">
                  <header className="dp-plan-block__head">
                    <div className="dp-plan-block__titles">
                      <h2 id="dp-plan-min-title" className="dp-plan-block__title">План «Минимум»</h2>
                      <p className="dp-plan-block__subtitle">Базовая выплата за выполнение всех целей</p>
                    </div>
                    <label className="dp-plan-block__reward">
                      <span className="dp-plan-block__reward-label">Выплата</span>
                      <span className="dp-plan-block__reward-control">
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          className="dp-plan-input"
                          value={editableDpValue(mcSettings.MRP?.plan_minimum_dp)}
                          onChange={(e) => handleDpRewardChange('plan_minimum_dp', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onBlur={() => handleDpRewardBlur('plan_minimum_dp')}
                          aria-label="DP за план Минимум"
                        />
                        <span>DP</span>
                      </span>
                    </label>
                  </header>

                  <div className="dp-plan-block__body">
                    <div className="dp-plan-rows" role="list">
                      {DP_PLAN_ACTIONS.map((action) => {
                        const count = mcSettings.MRP?.plan_minimum?.[action.key] ?? 0;
                        return (
                          <label key={`min-${action.key}`} className="dp-plan-row" role="listitem">
                            <span className="dp-plan-row__text">
                              <span className="dp-plan-row__verb">{action.verb}</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                placeholder="0"
                                className="dp-plan-input dp-plan-input--row"
                                value={editableDpValue(count)}
                                onChange={(e) => handleDpPlanTargetChange('plan_minimum', action.key, e.target.value)}
                                onFocus={(e) => e.target.select()}
                                onBlur={() => handleDpPlanTargetBlur('plan_minimum', action.key)}
                                aria-label={`${action.verb} ${action.unit}`}
                              />
                              <span className="dp-plan-row__unit">{action.unit}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </section>

                <section className="dp-plan-block dp-plan-block--max" aria-labelledby="dp-plan-max-title">
                  <header className="dp-plan-block__head">
                    <div className="dp-plan-block__titles">
                      <h2 id="dp-plan-max-title" className="dp-plan-block__title">План «Максимум»</h2>
                      <p className="dp-plan-block__subtitle">Дополнительная выплата сверху минимума</p>
                    </div>
                    <label className="dp-plan-block__reward">
                      <span className="dp-plan-block__reward-label">Дополнительно</span>
                      <span className="dp-plan-block__reward-control">
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          className="dp-plan-input"
                          value={editableDpValue(mcSettings.MRP?.plan_maximum_dp)}
                          onChange={(e) => handleDpRewardChange('plan_maximum_dp', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onBlur={() => handleDpRewardBlur('plan_maximum_dp')}
                          aria-label="DP за план Максимум"
                        />
                        <span>DP</span>
                      </span>
                    </label>
                  </header>

                  <div className="dp-plan-block__body">
                    <div className="dp-plan-rows" role="list">
                      {DP_PLAN_ACTIONS.map((action) => {
                        const count = mcSettings.MRP?.plan_maximum?.[action.key] ?? 0;
                        return (
                          <label key={`max-${action.key}`} className="dp-plan-row" role="listitem">
                            <span className="dp-plan-row__text">
                              <span className="dp-plan-row__verb">{action.verb}</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                placeholder="0"
                                className="dp-plan-input dp-plan-input--row"
                                value={editableDpValue(count)}
                                onChange={(e) => handleDpPlanTargetChange('plan_maximum', action.key, e.target.value)}
                                onFocus={(e) => e.target.select()}
                                onBlur={() => handleDpPlanTargetBlur('plan_maximum', action.key)}
                                aria-label={`${action.verb} ${action.unit}`}
                              />
                              <span className="dp-plan-row__unit">{action.unit}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </section>
              </div>

              <div className="dp-plans__actions">
                <button type="button" className="dp-plans__save" onClick={handleMcSettingsSave}>
                  Сохранить
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'mrp-rating' && hasFullSettingsAccess && (
        <SettingsMrpRating isActive setError={setError} setSuccess={setSuccess} />
      )}

      {activeTab === 'staff' && (
        <div className="settings-panel staff-setup">
          {staffLoading ? (
            <div className="loading">Загрузка…</div>
          ) : (
            <>
              <section className="settings-card">
                <h2 className="settings-card__title">Канал списка</h2>
                <div className="settings-rows">
                  <label className="settings-row" title="Куда бот публикует сообщение со составом">
                    <span className="settings-row__label">ID канала</span>
                    <input
                      type="text"
                      className="settings-row__input"
                      inputMode="numeric"
                      placeholder="ID"
                      value={staffListSettings.channel_id || ''}
                      onChange={(e) => handleStaffListSettingsChange({ channel_id: e.target.value })}
                      onFocus={(e) => e.target.select()}
                    />
                  </label>
                </div>
              </section>

              <section className="settings-card">
                <h2 className="settings-card__title" title="Ровно 5 мест — название должности и человек">
                  Старший состав
                </h2>
                <div className="staff-setup__slots">
                  {staffAssignments.slice(0, 5).map((assignment, index) => (
                    <div key={assignment.id} className="staff-setup__slot">
                      <div className="staff-setup__slot-main">
                        <span className="staff-setup__slot-num">{index + 1}</span>
                        <input
                          type="text"
                          className="staff-setup__input staff-setup__input--title"
                          value={assignment.display_name || ''}
                          placeholder={assignment.position}
                          onChange={(e) =>
                            updateStaffAssignmentLocal(assignment.id, {
                              display_name: e.target.value,
                            })
                          }
                          onFocus={(e) => e.target.select()}
                          aria-label={`Название должности ${index + 1}`}
                        />
                        <select
                          className="staff-setup__select"
                          value={assignment.user_discord_id || ''}
                          onChange={(e) =>
                            updateStaffAssignmentLocal(assignment.id, {
                              user_discord_id: e.target.value || null,
                              user: null,
                            })
                          }
                          aria-label={`Человек на должности ${index + 1}`}
                        >
                          <option value="">Не назначено</option>
                          {staffUsers.map((user) => (
                            <option key={user.id} value={user.discord_user_id}>
                              {user.discord_username}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="staff-setup__slot-toggles">
                        <MrpRatingToggle
                          label="Пустая строка после"
                          checked={Boolean(assignment.add_spacer_after)}
                          onChange={(e) =>
                            updateStaffAssignmentLocal(assignment.id, {
                              add_spacer_after: e.target.checked,
                            })
                          }
                        />
                        <MrpRatingToggle
                          label="Выговоры в Discord"
                          checked={assignment.show_reprimands_in_discord !== false}
                          onChange={(e) =>
                            updateStaffAssignmentLocal(assignment.id, {
                              show_reprimands_in_discord: e.target.checked,
                            })
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="settings-card">
                <div className="settings-card__head">
                  <h2
                    className="settings-card__title"
                    title="Разделы по точным именам ролей Discord. Человек попадает в первый подходящий раздел."
                  >
                    После старшего
                  </h2>
                  <button
                    type="button"
                    className="settings-btn settings-btn--ghost settings-card__action"
                    onClick={handleAddStaffRoleSection}
                    disabled={staffListSettings.role_sections.length >= 8}
                  >
                    + Раздел
                  </button>
                </div>
                <div className="staff-setup__sections">
                  {staffListSettings.role_sections.map((section, index) => (
                    <div key={`staff-section-${index}`} className="staff-setup__section">
                      <div className="staff-setup__section-head">
                        <span className="staff-setup__slot-num">{index + 1}</span>
                        <input
                          type="text"
                          className="staff-setup__input staff-setup__input--title"
                          value={section.title}
                          placeholder="Название раздела"
                          onChange={(e) =>
                            handleStaffRoleSectionChange(index, { title: e.target.value })
                          }
                          onFocus={(e) => e.target.select()}
                          aria-label={`Название раздела ${index + 1}`}
                        />
                        {staffListSettings.role_sections.length > 1 && (
                          <button
                            type="button"
                            className="settings-btn settings-btn--ghost staff-setup__remove"
                            onClick={() => handleRemoveStaffRoleSection(index)}
                            title="Удалить раздел"
                          >
                            Удалить
                          </button>
                        )}
                      </div>
                      <label className="staff-setup__field">
                        <span className="staff-setup__field-label">Роли Discord</span>
                        <textarea
                          className="staff-setup__textarea"
                          rows={2}
                          placeholder={'Manager\nJunior'}
                          value={(section.roles || []).join('\n')}
                          onChange={(e) =>
                            handleStaffRoleSectionChange(index, {
                              roles: e.target.value.split('\n'),
                            })
                          }
                        />
                      </label>
                      <div className="staff-setup__slot-toggles staff-setup__slot-toggles--single">
                        <MrpRatingToggle
                          label="Показывать выговоры"
                          checked={section.show_reprimands !== false}
                          onChange={(e) =>
                            handleStaffRoleSectionChange(index, {
                              show_reprimands: e.target.checked,
                            })
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <div className="settings-actions">
                <button
                  type="button"
                  className="settings-btn settings-btn--ghost"
                  onClick={handleStaffRefreshDiscord}
                  disabled={staffSaving}
                >
                  Обновить в Discord
                </button>
                <button
                  type="button"
                  className="settings-btn settings-btn--ghost"
                  onClick={fetchStaffData}
                  disabled={staffSaving}
                >
                  Отменить
                </button>
                <button
                  type="button"
                  className="settings-btn settings-btn--primary"
                  onClick={handleStaffSave}
                  disabled={staffSaving}
                >
                  {staffSaving ? '…' : 'Сохранить'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'display-settings' && hasFullSettingsAccess && (
        <SettingsDisplay isActive setError={setError} setSuccess={setSuccess} />
      )}

      {activeTab === 'data-export' && hasFullSettingsAccess && (
        <SettingsDataExport
          isActive
          setError={setError}
          setSuccess={setSuccess}
          userRoles={userRoles}
        />
      )}

      {activeTab === 'discord-ops' && hasFullSettingsAccess && (
        <SettingsDiscordOps isActive setError={setError} setSuccess={setSuccess} />
      )}

      {activeTab === 'message-relay' && (
        <SettingsMessageRelay isActive setError={setError} setSuccess={setSuccess} />
      )}

      {activeTab === 'stats-ingest' && (
        <SettingsStatsIngest isActive setError={setError} setSuccess={setSuccess} />
      )}

      {/* Содержимое вкладки "Вопросы" */}
      {activeTab === 'questions' && (
        <div className="questions-settings-container">
          {questionsLoading ? (
            <div className="loading">Загрузка вопросов...</div>
          ) : (
            <>
              <div className="add-button-container">
                <button className="btn btn-add-server" onClick={handleAddQuestion} type="button">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  <span>Добавить вопрос</span>
                </button>
              </div>

              {/* Модальное окно формы вопроса */}
              {showQuestionForm && (
                <div className="form-modal-overlay" onClick={resetQuestionForm}>
                  <div className="form-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="form-modal-header">
                      <h3>{editingQuestion ? 'Редактировать вопрос' : 'Добавить вопрос'}</h3>
                      <button type="button" className="btn-close-form" onClick={resetQuestionForm} title="Закрыть">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                    <form onSubmit={(e) => { e.preventDefault(); handleSaveQuestion(); }} className="form-modal-body">
                      <div className="form-group">
                        <label htmlFor="question_text">Текст вопроса *</label>
                        <textarea
                          id="question_text"
                          rows="4"
                          value={questionFormData.question_text}
                          onChange={(e) => setQuestionFormData({ ...questionFormData, question_text: e.target.value })}
                          required
                          placeholder="Введите текст вопроса..."
                          className="form-textarea"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="answer_text">Эталонный ответ *</label>
                        <textarea
                          id="answer_text"
                          rows="3"
                          value={questionFormData.answer_text}
                          onChange={(e) => setQuestionFormData({ ...questionFormData, answer_text: e.target.value })}
                          required
                          placeholder="Введите эталонный ответ..."
                          className="form-textarea"
                        />
                      </div>

                      <div className="form-modal-actions">
                        <button type="button" className="btn btn-secondary" onClick={resetQuestionForm}>
                          Отмена
                        </button>
                        <button type="submit" className="btn btn-primary">
                          {editingQuestion ? 'Сохранить изменения' : 'Создать вопрос'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Список вопросов */}
              <div className="questions-list">
                {questions.length === 0 ? (
                  <div className="empty-state">
                    <p>Вопросы не найдены. Создайте первый вопрос.</p>
                  </div>
                ) : (
                  <>
                    {/* Вводный вопрос */}
                    {questions.filter(q => q.is_intro).map(question => (
                      <div key={question.id} className="question-card question-intro">
                        <div className="question-card-header">
                          <span className="question-badge question-badge-intro">Вводный</span>
                          <div className="question-actions">
                            <button
                              type="button"
                              className="btn-icon btn-edit"
                              onClick={() => handleEditQuestion(question)}
                              title="Редактировать"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="btn-icon btn-delete"
                              onClick={() => handleDeleteQuestion(question.id)}
                              title="Удалить"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="question-card-body">
                          <p className="question-text">{question.question_text}</p>
                        </div>
                      </div>
                    ))}

                    {/* Оцениваемые вопросы */}
                    {questions.filter(q => !q.is_intro).map((question, index) => (
                      <div key={question.id} className="question-card">
                        <div className="question-card-header">
                          <span className="question-badge">Вопрос #{index + 1}</span>
                          <div className="question-actions">
                            <button
                              type="button"
                              className="btn-icon btn-edit"
                              onClick={() => handleEditQuestion(question)}
                              title="Редактировать"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="btn-icon btn-delete"
                              onClick={() => handleDeleteQuestion(question.id)}
                              title="Удалить"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="question-card-body">
                          <p className="question-text">{question.question_text}</p>
                          <div className="question-answer">
                            <strong>Эталонный ответ:</strong>
                            <p>{question.answer_text}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default Settings;

